// 소유 모듈에서 직접 가져온다. 전역 배럴(`@/api`) 의존에는 예산 제한이 있다
// (`scripts/check-boundaries.mjs`).
import { DATASET_FILE_SIGN_MAX } from "@/api/datasetFileSign";
import { parseUtcMs } from "@/utils/datetime";
import type { DirectoryWriter } from "./directoryWriter";
import {
  buildDownloadPlan,
  type DownloadEntry,
  type DownloadPlan,
  type DownloadSelection,
  type DownloadSourceFiles,
} from "./downloadPlan";

/**
 * 대량 다운로드 엔진. 대상 수집과 실행을 분리한다.
 *
 * 실행이 entries 를 받으므로, 실패분 재시도는 목록 재수집(파일 수 / 100 왕복) 없이 끝난다.
 * 전송·서명은 transport 로 주입한다. 그래서 React 와 네트워크 없이 검증할 수 있다.
 * 실행은 경로·크기·URL 만 읽는다. 그래서 Dataset 파일이 아닌 파일도 같은 엔진으로 받는다.
 * 예: Annotation Set 파일.
 */

// 브라우저 호스트당 연결 한계(6)에 맞춘다. 더 올려도 실제 병렬도가 늘지 않는다.
export const DOWNLOAD_CONCURRENCY = 6;

// 이 여유 안에 만료되는 URL 은 미리 재서명한다. 창 단위 서명에서는 방금 받은 URL 이므로
// 이 경로는 거의 실행되지 않는다. 창 하나가 오래 걸릴 때를 대비해 유지한다.
const EXPIRY_MARGIN_MS = 60_000;

// 한 번에 서명할 파일 수. 서버 상한과 같다(`DATASET_FILE_SIGN_MAX`). 늘리면 만료 여유가
// 줄고, 줄이면 요청이 늘어난다.
export const SIGN_WINDOW = DATASET_FILE_SIGN_MAX;

// 실행이 읽는 필드만 남긴 항목이다. 저장 경로, 재개 판정 크기, signed URL 과 그 만료를 담는다.
export type RunnableDownloadEntry = Pick<
  DownloadEntry,
  "relativePath" | "sizeBytes" | "url" | "expiresAt"
>;

// 서명을 마친 창의 한 줄. 받을 수 없는 파일은 url 이 null 이고 사유가 붙는다.
// 배치 전체가 실패하지 않으므로 행마다 확인한다(BE spec §3.3).
export type SignedDownloadEntry<Entry extends RunnableDownloadEntry = DownloadEntry> = Entry & {
  unavailableReason: string | null;
};

// 실행에 필요한 서명·전송만 담은 transport.
export type DownloadFileTransport<Entry extends RunnableDownloadEntry> = {
  // 곧 받을 창 하나를 한 번에 서명한다. 응답은 입력과 같은 길이·순서다.
  signWindow: (
    entries: readonly Entry[],
    signal: AbortSignal,
  ) => Promise<SignedDownloadEntry<Entry>[]>;
  // 창 안에서 만료를 맞은 파일 하나를 다시 서명한다(403 폴백).
  resign: (entry: Entry, signal: AbortSignal) => Promise<string>;
  fetchBody: (url: string, signal: AbortSignal) => Promise<ReadableStream<Uint8Array>>;
};

export type DownloadTransport = DownloadFileTransport<DownloadEntry> & {
  // canonical episode · meta · extra 세 목록을 **서명 없이** 모아 온다(메타 스냅샷).
  collect: (signal: AbortSignal) => Promise<DownloadSourceFiles>;
};

export type DownloadFailure<Entry extends RunnableDownloadEntry = DownloadEntry> = {
  entry: Entry;
  message: string;
};

export type DownloadProgress = {
  total: number;
  // 새로 받은 파일 수.
  completed: number;
  // 이미 같은 크기로 있어 건너뛴 파일 수(재개).
  skipped: number;
  failed: number;
};

export type DownloadOutcome<Entry extends RunnableDownloadEntry = DownloadEntry> =
  DownloadProgress & {
    failures: DownloadFailure<Entry>[];
    cancelled: boolean;
  };

export type RunDownloadOptions = {
  signal: AbortSignal;
  onProgress?: (progress: DownloadProgress) => void;
  concurrency?: number;
};

export async function collectDownloadPlan(
  transport: DownloadTransport,
  selection: DownloadSelection,
  signal: AbortSignal,
): Promise<DownloadPlan> {
  return buildDownloadPlan(await transport.collect(signal), selection);
}

export async function runDownload<Entry extends RunnableDownloadEntry>(
  transport: DownloadFileTransport<Entry>,
  writer: DirectoryWriter,
  entries: readonly Entry[],
  { signal, onProgress, concurrency = DOWNLOAD_CONCURRENCY }: RunDownloadOptions,
): Promise<DownloadOutcome<Entry>> {
  const progress: DownloadProgress = {
    total: entries.length,
    completed: 0,
    skipped: 0,
    failed: 0,
  };
  const failures: DownloadFailure<Entry>[] = [];
  const report = (): void => onProgress?.({ ...progress });

  // 창 단위로 "서명 → 받기" 를 반복한다. 전체를 미리 서명하면 큐 뒤쪽 URL 이 TTL 을
  // 넘겨 만료된다(BE spec §5).
  for (let start = 0; start < entries.length; start += SIGN_WINDOW) {
    if (signal.aborted) break;
    const window = entries.slice(start, start + SIGN_WINDOW);

    // 이미 받은 파일은 **서명하기 전에** 걸러 낸다. 재개할 때 창마다 쓰지 않을 URL 을
    // 다시 서명하면, 재시작할 때마다 서버 CPU 만 소모한다.
    const pending = await pendingEntries(writer, window, concurrency, () => {
      progress.skipped += 1;
      report();
    });
    if (pending.length === 0 || signal.aborted) continue;

    let signed: SignedDownloadEntry<Entry>[];
    try {
      signed = await transport.signWindow(pending, signal);
    } catch (error) {
      if (signal.aborted) break;
      // 창 하나의 서명 전체가 실패했다. 그 창의 파일을 모두 실패로 세고 다음 창으로
      // 이동한다. 여기서 멈추면 뒤쪽 창은 시도하지 못한다.
      for (const entry of pending) {
        progress.failed += 1;
        failures.push({ entry, message: errorMessage(error) });
      }
      report();
      continue;
    }

    await runPool(signed, concurrency, async (entry) => {
      if (signal.aborted) return;
      try {
        if (entry.unavailableReason !== null) {
          throw new Error(entry.unavailableReason);
        }
        await downloadOne(transport, writer, entry, signal);
        progress.completed += 1;
      } catch (error) {
        // 취소는 실패가 아니다. 남은 항목을 실패로 세면 사용자가 오해한다.
        if (signal.aborted) return;
        progress.failed += 1;
        failures.push({ entry, message: errorMessage(error) });
      }
      report();
    });
  }

  return { ...progress, failures, cancelled: signal.aborted };
}

/**
 * 창에서 아직 받지 않은 항목만 남긴다. 순서는 입력 그대로다.
 *
 * 완료 순서로 모으면 서명 요청의 행 순서가 실행마다 달라져 재현이 어려워진다. 그래서
 * 판정만 병렬로 하고, 걸러내기는 인덱스로 한다.
 */
async function pendingEntries<Entry extends RunnableDownloadEntry>(
  writer: DirectoryWriter,
  window: readonly Entry[],
  concurrency: number,
  onSkip: () => void,
): Promise<Entry[]> {
  const keep = new Array<boolean>(window.length).fill(false);
  await runPool(
    window.map((_entry, index) => index),
    concurrency,
    async (index) => {
      const entry = window[index];
      if (await writer.isAlreadyWritten(entry.relativePath, entry.sizeBytes)) {
        onSkip();
        return;
      }
      keep[index] = true;
    },
  );
  return window.filter((_entry, index) => keep[index]);
}

async function downloadOne<Entry extends RunnableDownloadEntry>(
  transport: DownloadFileTransport<Entry>,
  writer: DirectoryWriter,
  entry: Entry,
  signal: AbortSignal,
): Promise<void> {
  let url = isUsable(entry) ? (entry.url as string) : await transport.resign(entry, signal);
  try {
    await writer.write(entry.relativePath, await transport.fetchBody(url, signal));
  } catch (error) {
    // 여유를 두었는데도 만료되었으면 한 번 더 서명해 재시도한다. TTL 이 1시간이므로 큐가 길면
    // 정상 경로에서도 발생한다.
    if (signal.aborted || !isExpiredResponse(error)) throw error;
    url = await transport.resign(entry, signal);
    await writer.write(entry.relativePath, await transport.fetchBody(url, signal));
  }
}

// 표시가 아니라 재서명 여부를 정하는 게이트이므로 공용 파서를 쓴다. zone 없는 문자열을
// 로컬로 읽으면 만료 시각이 뷰어 offset 만큼 과거로 이동한다. 그러면 모든 URL 이 stale 로
// 보이고, 파일마다 재서명 왕복이 실행된다(조용한 network N+1). 파싱할 수 없으면 보수적으로
// 만료로 본다.
function isUsable(entry: RunnableDownloadEntry): boolean {
  if (entry.url === null) return false;
  if (entry.expiresAt === null) return true;
  const expiresAt = parseUtcMs(entry.expiresAt);
  if (expiresAt === null) return false;
  return expiresAt - Date.now() > EXPIRY_MARGIN_MS;
}

// storage 는 만료된 signed URL 에 403 을 준다.
function isExpiredResponse(error: unknown): boolean {
  return error instanceof DownloadHttpError && error.status === 403;
}

// storage 에 직접 GET 한다. registry 를 거치지 않으므로 apiClient(인증 헤더)를 쓰지 않는다.
export async function fetchStorageBody(
  url: string,
  signal: AbortSignal,
): Promise<ReadableStream<Uint8Array>> {
  const response = await fetch(url, { signal });
  if (!response.ok) throw new DownloadHttpError(response.status);
  if (response.body === null) throw new Error("storage response had no body");
  return response.body;
}

export class DownloadHttpError extends Error {
  readonly status: number;

  constructor(status: number) {
    super(`storage download failed with status ${status}`);
    this.name = "DownloadHttpError";
    this.status = status;
  }
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

// 고정 크기 워커 풀. 항목 수만큼 promise 를 한꺼번에 만들지 않으므로 큐가 커져도 메모리가 늘지 않는다.
async function runPool<Item>(
  items: readonly Item[],
  limit: number,
  worker: (item: Item) => Promise<void>,
): Promise<void> {
  let next = 0;
  const size = Math.max(1, Math.min(limit, items.length));
  await Promise.all(
    Array.from({ length: size }, async () => {
      while (next < items.length) {
        // JS 는 단일 스레드이므로 읽기와 증가 사이에 다른 워커가 실행되지 않는다.
        const index = next;
        next += 1;
        await worker(items[index]);
      }
    }),
  );
}
